import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { TextField, InputAdornment, Icon, MenuItem, Chip, Pagination } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DialogShell from "./DialogShell";
import { useICCCColors, ICCC_COLORS as ICCC_FALLBACK } from "../theme";
import { SEVERITY_LABEL, HEALTH, severityToHealth, categoryMeta } from "../../model/constants";

const PAGE_SIZE = 20;

const fmt = (ms) => {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const SeverityChip = ({ severity }) => {
  const color = ICCC_FALLBACK[severityToHealth(severity)] || ICCC_FALLBACK.unknown;
  return (
    <span
      style={{
        background: color,
        color: "#fff",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        display: "inline-block",
        minWidth: 64,
        textAlign: "center",
      }}
    >
      {SEVERITY_LABEL[Number(severity)] || "Unknown"}
    </span>
  );
};
SeverityChip.propTypes = { severity: PropTypes.number };

const FILTERS = [
  { value: "all", label: "All severities" },
  { value: HEALTH.CRITICAL, label: "Critical" },
  { value: HEALTH.WARNING, label: "Warning" },
  { value: HEALTH.UNKNOWN, label: "Unknown" },
];

const SummaryStat = ({ label, value, color, colors }) => (
  <MDBox
    sx={{
      flex: 1,
      px: 1.5,
      py: 1,
      borderRadius: 1,
      bgcolor: colors.cardBg2,
      border: `1px solid ${colors.cardBorder}`,
      borderLeft: `3px solid ${color}`,
      minWidth: 0,
    }}
  >
    <MDTypography
      variant="caption"
      sx={{ color: colors.textMuted, fontSize: 11, display: "block", lineHeight: 1.2 }}
    >
      {label}
    </MDTypography>
    <MDTypography
      variant="h6"
      fontWeight="bold"
      sx={{ color, lineHeight: 1.2, mt: 0.25 }}
    >
      {Number(value || 0).toLocaleString("en-IN")}
    </MDTypography>
  </MDBox>
);
SummaryStat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  color: PropTypes.string,
  colors: PropTypes.object,
};

export default function AlertsDialog({ config, open, onClose, onSelectDevice }) {
  const ICCC_COLORS = useICCCColors();
  const [sevFilter, setSevFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [sevFilter, search, config?.alerts]);

  const th = {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: `2px solid ${ICCC_COLORS.divider}`,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: ICCC_COLORS.textMuted,
    whiteSpace: "nowrap",
    background: ICCC_COLORS.cardBg2,
  };
  const td = {
    padding: "10px 12px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    color: ICCC_COLORS.textPrimary,
    verticalAlign: "middle",
    fontSize: 12.5,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: ICCC_COLORS.textPrimary,
      bgcolor: ICCC_COLORS.cardBg,
      "& fieldset": { borderColor: ICCC_COLORS.cardBorder },
      "&:hover fieldset": { borderColor: ICCC_COLORS.accent },
      "&.Mui-focused fieldset": { borderColor: ICCC_COLORS.accent, borderWidth: 1.5 },
    },
    "& input::placeholder": { color: ICCC_COLORS.textMuted, opacity: 1 },
    "& .MuiSvgIcon-root": { color: ICCC_COLORS.textSecondary },
  };

  const all = config?.alerts || [];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((a) => {
      if (sevFilter !== "all" && a.health !== sevFilter) return false;
      if (!q) return true;
      // Case-insensitive search across every visible field.
      return [
        a.message,
        a.nodeName,
        a.ipAddress,
        a.category,
        categoryMeta(a.category).label,
        SEVERITY_LABEL[Number(a.severity)],
      ].some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [all, sevFilter, search]);

  const totals = useMemo(
    () => ({
      total: all.length,
      critical: all.filter((a) => a.health === HEALTH.CRITICAL).length,
      warning: all.filter((a) => a.health === HEALTH.WARNING).length,
      unknown: all.filter((a) => a.health === HEALTH.UNKNOWN).length,
    }),
    [all]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(startIdx, startIdx + PAGE_SIZE);
  const showFrom = rows.length === 0 ? 0 : startIdx + 1;
  const showTo = Math.min(startIdx + PAGE_SIZE, rows.length);

  if (!config) return null;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title={config.title || "Active Alerts"}
      subtitle={`${totals.total.toLocaleString("en-IN")} alerts across all groups`}
      icon="notifications_active"
      color={ICCC_COLORS.critical}
      maxWidth="lg"
    >
      <MDBox display="flex" gap={1.25} mb={2}>
        <SummaryStat label="Total"    value={totals.total}    color={ICCC_COLORS.accent}   colors={ICCC_COLORS} />
        <SummaryStat label="Critical" value={totals.critical} color={ICCC_COLORS.critical} colors={ICCC_COLORS} />
        <SummaryStat label="Warning"  value={totals.warning}  color={ICCC_COLORS.warning}  colors={ICCC_COLORS} />
        <SummaryStat label="Unknown"  value={totals.unknown}  color={ICCC_COLORS.unknown}  colors={ICCC_COLORS} />
      </MDBox>

      <MDBox display="flex" gap={1.5} mb={1.5}>
        <TextField
          size="small"
          placeholder="Search message, device, group..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, ...inputSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small" sx={{ color: ICCC_COLORS.textMuted }}>search</Icon>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={sevFilter}
          onChange={(e) => setSevFilter(e.target.value)}
          sx={{ minWidth: 180, ...inputSx }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  bgcolor: ICCC_COLORS.cardBg,
                  color: ICCC_COLORS.textPrimary,
                  border: `1px solid ${ICCC_COLORS.cardBorder}`,
                  boxShadow: ICCC_COLORS.shadow,
                },
              },
            },
          }}
        >
          {FILTERS.map((f) => (
            <MenuItem
              key={f.value}
              value={f.value}
              sx={{ "&.Mui-selected": { bgcolor: `${ICCC_COLORS.accent}1f` } }}
            >
              {f.label}
            </MenuItem>
          ))}
        </TextField>
      </MDBox>

      <MDBox
        sx={{
          border: `1px solid ${ICCC_COLORS.cardBorder}`,
          borderRadius: 1.5,
          bgcolor: ICCC_COLORS.cardBg,
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              {["Severity", "Device", "Group", "Message", "Raised"].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted, padding: 32 }}>
                  <Icon sx={{ fontSize: 32, color: ICCC_COLORS.textDim, display: "block", mx: "auto", mb: 0.5 }}>
                    inbox
                  </Icon>
                  No alerts match the current filters
                </td>
              </tr>
            )}
            {pageRows.map((a) => (
              <tr
                key={a.id}
                onClick={() =>
                  onSelectDevice?.({
                    id: a.assetId,
                    uuid: a.uuid,
                    nodeName: a.nodeName,
                    category: a.category,
                    ipAddress: a.ipAddress,
                    location: a.location,
                    health: a.health,
                  })
                }
                style={{ cursor: "pointer", transition: "background 100ms ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = ICCC_COLORS.hoverBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                title="View device details"
              >
                <td style={td}><SeverityChip severity={a.severity} /></td>
                <td style={{ ...td, fontWeight: 600 }}>{a.ipAddress || a.nodeName || `Asset #${a.assetId}`}</td>
                <td style={td}>
                  <Chip
                    size="small"
                    label={categoryMeta(a.category).label}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: `${ICCC_COLORS.accent}14`,
                      color: ICCC_COLORS.accent,
                      border: `1px solid ${ICCC_COLORS.accent}33`,
                    }}
                  />
                </td>
                <td style={{ ...td, color: ICCC_COLORS.textSecondary }}>{a.message}</td>
                <td style={{ ...td, color: ICCC_COLORS.textSecondary, whiteSpace: "nowrap" }}>{fmt(a.raisedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </MDBox>

      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mt={1.5}
      >
        <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textMuted }}>
          Showing{" "}
          <strong style={{ color: ICCC_COLORS.textPrimary }}>
            {showFrom.toLocaleString("en-IN")}–{showTo.toLocaleString("en-IN")}
          </strong>{" "}
          of {rows.length.toLocaleString("en-IN")} alert{rows.length === 1 ? "" : "s"}
          {rows.length !== all.length && (
            <> (filtered from {all.length.toLocaleString("en-IN")})</>
          )}
        </MDTypography>

        <Pagination
          count={totalPages}
          page={safePage}
          onChange={(_, p) => setPage(p)}
          size="small"
          siblingCount={1}
          boundaryCount={1}
          showFirstButton
          showLastButton
          shape="rounded"
          sx={{
            "& .MuiPaginationItem-root": {
              color: ICCC_COLORS.textSecondary,
              borderColor: ICCC_COLORS.cardBorder,
              fontWeight: 600,
            },
            "& .MuiPaginationItem-root:hover": {
              bgcolor: ICCC_COLORS.hoverBg,
              borderColor: ICCC_COLORS.accent,
            },
            "& .MuiPaginationItem-root.Mui-selected": {
              bgcolor: ICCC_COLORS.accent,
              color: "#fff",
              borderColor: ICCC_COLORS.accent,
              "&:hover": { bgcolor: ICCC_COLORS.accent, opacity: 0.92 },
            },
            "& .MuiPaginationItem-ellipsis": { color: ICCC_COLORS.textMuted },
          }}
        />
      </MDBox>
    </DialogShell>
  );
}

AlertsDialog.propTypes = {
  config: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSelectDevice: PropTypes.func,
};
